console.log('[cart-drawer-upsell] Asset loaded', { version: 'v3' });

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define(
    'cart-drawer-upsell',
    class CartDrawerUpsell extends HTMLElement {
      connectedCallback() {
        console.log('[cart-drawer-upsell] Component connected', this);
        this.track = this.querySelector('[data-cart-upsell-track]');
        this.status = this.querySelector('[data-cart-upsell-status]');
        this.enableMouseDrag();
        this.querySelector('[data-cart-upsell-previous]')?.addEventListener('click', () => this.scroll(-1));
        this.querySelector('[data-cart-upsell-next]')?.addEventListener('click', () => this.scroll(1));
        this.loadDynamicRecommendations();
      }

      enableMouseDrag() {
        if (!this.track) return;

        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let dragged = false;
        this.track.dataset.cartUpsellDragged = 'false';

        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (event.target.closest('button, a, input, select, textarea, [role="button"]')) {
            pointerId = null;
            dragged = false;
            this.track.dataset.cartUpsellDragged = 'false';
            this.track.classList.remove('is-dragging');
            return;
          }

          pointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = this.track.scrollLeft;
          dragged = false;
          this.track.dataset.cartUpsellDragged = 'false';
        });

        this.track.addEventListener('pointermove', (event) => {
          if (event.pointerId !== pointerId) return;

          const distance = event.clientX - startX;
          if (!dragged && Math.abs(distance) <= 5) return;
          if (!dragged) {
            dragged = true;
            this.track.classList.add('is-dragging');
            this.track.setPointerCapture(pointerId);
          }

          this.track.dataset.cartUpsellDragged = 'true';
          event.preventDefault();
          this.track.scrollLeft = startScrollLeft - distance;
        });

        const finishDrag = (event) => {
          if (event.pointerId !== pointerId) return;
          pointerId = null;
          dragged = false;
          this.track.dataset.cartUpsellDragged = 'false';
          this.track.classList.remove('is-dragging');
        };

        this.track.addEventListener('pointerup', finishDrag);
        this.track.addEventListener('pointercancel', finishDrag);
        this.track.addEventListener('lostpointercapture', finishDrag);
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
        this.track.addEventListener(
          'click',
          (event) => {
            this.track.classList.remove('is-dragging');
            if (!dragged) return;
            event.preventDefault();
            event.stopPropagation();
            dragged = false;
            this.track.dataset.cartUpsellDragged = 'false';
          },
          true
        );
      }

      async loadDynamicRecommendations() {
        if (!this.track) return;

        const cartProductIds = (this.dataset.cartProductIds || '')
          .split(',')
          .map(Number)
          .filter(Boolean);

        if (!cartProductIds.length) return;

        const recommendationLists = await Promise.all(
          cartProductIds.map(async (productId) => {
            const complementary = await this.fetchRecommendations(productId, 'complementary');
            return complementary.length ? complementary : this.fetchRecommendations(productId, 'related');
          })
        );
        const populatedLists = recommendationLists.filter((products) => products.length);
        if (!populatedLists.length) return;

        const commonProducts = populatedLists[0].filter((product) =>
          populatedLists.every((products) => products.some((candidate) => candidate.id === product.id))
        );
        const recommendedProducts = commonProducts.length ? commonProducts : populatedLists.flat();
        const seenProductIds = new Set([
          ...cartProductIds,
          ...Array.from(this.track.querySelectorAll('[data-product-id]')).map((card) => Number(card.dataset.productId)),
        ]);

        recommendedProducts.some((product) => {
          if (seenProductIds.has(product.id)) return false;
          const variant = product.variants?.find((candidate) => candidate.available);
          if (!variant) return false;

          seenProductIds.add(product.id);
          this.track.appendChild(this.createRecommendationCard(product, variant));
          return this.track.querySelectorAll('[data-product-id]').length >= 20;
        });

        if (this.track.querySelector('[data-product-id]')) this.hidden = false;
      }

      async fetchRecommendations(productId, intent) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(
            `${root}recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`
          );
          if (!response.ok) return [];
          const data = await response.json();
          return Array.isArray(data.products) ? data.products : [];
        } catch (error) {
          return [];
        }
      }

      createRecommendationCard(product, variant) {
        const card = document.createElement('article');
        card.className = 'cart-drawer-upsell__card mini-cart-fbt-card';
        card.dataset.productId = product.id;

        const imageLink = document.createElement('a');
        imageLink.className = 'cart-drawer-upsell__image-link';
        imageLink.href = product.url;
        imageLink.tabIndex = -1;

        const image = document.createElement('img');
        image.className = 'cart-drawer-upsell__image';
        image.src = variant.featured_image?.src || product.featured_image || '';
        image.alt = product.title;
        image.loading = 'lazy';
        image.width = 80;
        image.height = 80;
        imageLink.appendChild(image);

        const content = document.createElement('div');
        content.className = 'cart-drawer-upsell__content';
        const details = document.createElement('div');
        const title = document.createElement('h4');
        title.className = 'cart-drawer-upsell__title';
        const titleLink = document.createElement('a');
        titleLink.href = product.url;
        titleLink.textContent = product.title;
        title.appendChild(titleLink);

        const price = document.createElement('p');
        price.className = 'cart-drawer-upsell__price';
        price.textContent = this.formatMoney(variant.price);
        details.append(title, price);

        const addButton = document.createElement('button');
        addButton.type = 'button';
        addButton.className = 'cart-drawer-upsell__add mini-cart-fbt-card__cart-btn';
        addButton.dataset.cartUpsellAdd = '';
        addButton.dataset.variantId = variant.id;
        const addLabel = document.createElement('span');
        addLabel.textContent = 'Add';
        addButton.appendChild(addLabel);

        content.append(details, addButton);
        card.append(imageLink, content);
        return card;
      }

      formatMoney(cents) {
        const currency = window.Shopify?.currency?.active || 'USD';
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(Number(cents) / 100);
      }

      scroll(direction) {
        if (!this.track) return;
        this.track.scrollBy({
          left: direction * Math.max(this.track.clientWidth * 0.75, 216),
          behavior: 'smooth',
        });
      }

      async addProduct(button) {
        console.log('[cart-drawer-upsell] addProduct called', {
          button,
          variantId: button.dataset.variantId,
          disabled: button.disabled,
          ariaDisabled: button.getAttribute('aria-disabled'),
        });
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const cartDrawer = document.querySelector('cart-drawer');
        const variantId = Number(button.dataset.variantId);
        if (!variantId) {
          console.error('[cart-drawer-upsell] Missing variant ID', button);
          return;
        }

        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.classList.add('loading');

        try {
          const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];
          const cartAddUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
          const requestConfig =
            typeof fetchConfig === 'function'
              ? fetchConfig('javascript')
              : {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
                };
          requestConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
          const response = await fetch(cartAddUrl, {
            ...requestConfig,
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections,
              sections_url: window.location.pathname,
            }),
          });
          console.log('[cart-drawer-upsell] AJAX response received', { status: response.status, ok: response.ok });
          const cartData = await response.json();

          if (!response.ok || cartData.status) {
            throw new Error(cartData.description || window.cartStrings?.error || 'Unable to add this product to the cart.');
          }

          if (cartDrawer) {
            cartDrawer.setActiveElement(button);
            cartDrawer.renderContents(cartData);
          } else {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-drawer-upsell',
              productVariantId: variantId,
              cartData,
            });
          }
        } catch (error) {
          console.error('[cart-drawer-upsell] Add failed', error);
          if (this.status) this.status.textContent = error.message;
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-busy');
          button.classList.remove('loading');
        }
      }
    }
  );
}

if (!window.__cartDrawerUpsellAddClickBoundV3) {
  window.__cartDrawerUpsellAddClickBoundV3 = true;
  console.log('[cart-drawer-upsell] V3 capture listener bound');
  document.addEventListener('click', (event) => {
    const button = event.target?.closest?.('.cart-drawer-upsell__add, .mini-cart-fbt-card__cart-btn');
    if (!button) return;

    console.log('[cart-drawer-upsell] Button click captured', {
      target: event.target,
      button,
      variantId: button.dataset.variantId,
      hasAddAttribute: button.hasAttribute('data-cart-upsell-add'),
      classes: button.className,
    });

    const isAjaxButton =
      button.hasAttribute('data-cart-upsell-add') ||
      (button.classList.contains('mini-cart-fbt-card__cart-btn') && Boolean(button.dataset.variantId));
    if (!isAjaxButton) return;

    const track = button.closest('[data-cart-upsell-track]');
    if (track?.dataset.cartUpsellDragged === 'true') {
      console.log('[cart-drawer-upsell] Click suppressed after drag');
      track.dataset.cartUpsellDragged = 'false';
      track.classList.remove('is-dragging');
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const upsell = button.closest('cart-drawer-upsell');
    if (!upsell || typeof upsell.addProduct !== 'function') {
      console.error('[cart-drawer-upsell] Add handler unavailable', { button, upsell });
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    console.log('[cart-drawer-upsell] Starting AJAX add', { variantId: button.dataset.variantId });
    void upsell.addProduct(button);
  }, true);
} else {
  console.log('[cart-drawer-upsell] V3 capture listener already exists');
}

if (!customElements.get('cart-discount-form')) {
  customElements.define(
    'cart-discount-form',
    class CartDiscountForm extends HTMLElement {
      connectedCallback() {
        this.syncVisibility();
        this.appObserver = new MutationObserver(() => this.syncVisibility());
        this.appObserver.observe(this.parentElement, { childList: true, subtree: true });

        this.querySelector('form')?.addEventListener('submit', (event) => {
          event.preventDefault();
          const code = this.querySelector('input')?.value.trim();
          if (!code) return;
          window.location.href = `${window.Shopify.routes.root}discount/${encodeURIComponent(code)}?redirect=/checkout`;
        });
      }

      disconnectedCallback() {
        this.appObserver?.disconnect();
      }

      syncVisibility() {
        this.hidden = Boolean(this.parentElement?.querySelector('.scDiscount__container'));
      }
    }
  );
}
